import { useQuery, useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import { queryKeys } from '@/lib/query-keys';
import { captureError } from '@/lib/sentry';

interface Workspace {
  id: string;
  name: string;
  created_at: string;
  created_by: string;
  plan_id?: string;
  is_personal: boolean;
  is_default?: boolean;
  member_count: number;
  contact_count: number;
  role: string;
  sso_owner_label?: string | null;
}

/**
 * SIGNED_IN priority: forceWorkspaceId → default → localStorage → personal → first.
 *
 * `forceWorkspaceId` is set by the enterprise SSO AutoLogin flow (via
 * sessionStorage 'ssoSignInWorkspaceId') and must beat the is_default flag,
 * since SSO users are clicking through to a specific enterprise workspace
 * and expect to land there regardless of any default they've set on a
 * personal workspace.
 */
export function pickWorkspaceOnSignIn(
  list: Workspace[],
  savedWorkspaceId: string | null,
  forceWorkspaceId: string | null = null,
): Workspace | null {
  if (list.length === 0) return null;
  if (forceWorkspaceId) {
    const forced = list.find(w => w.id === forceWorkspaceId);
    if (forced) return forced;
  }
  const def = list.find(w => w.is_default === true);
  if (def) return def;
  if (savedWorkspaceId) {
    const saved = list.find(w => w.id === savedWorkspaceId);
    if (saved) return saved;
  }
  return list.find(w => w.is_personal) ?? list[0];
}

/** Mid-session refresh priority: localStorage → default → personal → first */
export function pickWorkspaceOnRefresh(
  list: Workspace[],
  savedWorkspaceId: string | null,
): Workspace | null {
  if (list.length === 0) return null;
  if (savedWorkspaceId) {
    const saved = list.find(w => w.id === savedWorkspaceId);
    if (saved) return saved;
  }
  const def = list.find(w => w.is_default === true);
  if (def) return def;
  return list.find(w => w.is_personal) ?? list[0];
}

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const GC_TIME = 10 * 60 * 1000; // 10 minutes

async function fetchWorkspaces(userId: string): Promise<Workspace[]> {
  // Fetch workspaces that the user is a member of
  const { data: workspaceData, error: workspaceError } = await supabase.rpc(
    'get_workspace_details',
    { p_user_id: userId }
  );

  if (workspaceError) throw workspaceError;
  
  if (!workspaceData || workspaceData.length === 0) {
    // If no workspaces exist, create a personal workspace
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('User not authenticated');

    const { data: workspace, error: createError } = await supabase
      .from('workspaces')
      .insert([{
        name: `${user.user.email}'s Workspace`,
        created_by: userId,
        is_personal: true,
      }])
      .select()
      .single();

    if (createError) throw createError;

    const { error: memberError } = await supabase
      .from('workspace_members')
      .insert({
        workspace_id: workspace.id,
        user_id: userId,
        role: 'admin',
        status: 'active',
      });

    if (memberError) throw memberError;

    const newWorkspace = {
      ...workspace,
      role: 'admin',
      member_count: 1,
      contact_count: 0,
    };

    return [newWorkspace];
  }

  return workspaceData;
}

async function switchWorkspaceAction(workspaceId: string): Promise<void> {
  localStorage.setItem('currentWorkspaceId', workspaceId);
  // Force a page reload to refresh all data
  window.location.reload();
}

export function useWorkspaces() {
  const { user } = useAuth();
  const { toast } = useToast();

  // Initialize with partial workspace containing ID from localStorage
  // This allows dependent queries to start immediately while full data loads
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(() => {
    const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
    if (savedWorkspaceId) {
      // Return partial workspace with just ID to enable dependent queries
      return { id: savedWorkspaceId } as Workspace;
    }
    return null;
  });

  // Store the saved workspace ID for lookup after data loads
  const savedWorkspaceIdRef = useRef<string | null>(
    localStorage.getItem('currentWorkspaceId')
  );

  // Track whether we've already resolved a workspace this session.
  // Fresh sign-in (or first-ever load) uses pickWorkspaceOnSignIn so the
  // default flag wins; subsequent re-runs (refresh, refetch, page reload
  // after switchWorkspace) use pickWorkspaceOnRefresh so localStorage wins
  // (in-session continuity). Backed by sessionStorage so the flag survives
  // page reloads in the same tab — without this, every reload would re-run
  // pickWorkspaceOnSignIn and the is_default flag would snap the user back
  // to the default workspace, defeating the manual switch. Cleared on
  // SIGNED_OUT in AuthContext so a true fresh sign-in starts clean.
  const hasResolvedThisSessionRef = useRef(
    typeof window !== 'undefined' &&
      window.sessionStorage.getItem('workspaceResolvedSession') === '1',
  );

  // Fetch workspaces query
  const {
    data: workspaces = [],
    isPending: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.workspaces.list(user?.id),
    queryFn: () => fetchWorkspaces(user!.id),
    enabled: !!user?.id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('auth') || (error as any)?.status === 401) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    throwOnError: false,
  });

  // Use an effect to properly update currentWorkspace when workspaces are loaded
  useEffect(() => {
    if (workspaces.length === 0) return;

    const savedWorkspaceId =
      savedWorkspaceIdRef.current || localStorage.getItem('currentWorkspaceId');

    if (currentWorkspace?.name) {
      const matched = workspaces.find(w => w.id === currentWorkspace.id);
      if (matched) {
        setCurrentWorkspace(matched);
        return;
      }
    }

    let picked: Workspace | null;
    if (hasResolvedThisSessionRef.current) {
      picked = pickWorkspaceOnRefresh(workspaces, savedWorkspaceId);
    } else {
      // One-shot SSO override: AutoLogin sets this when an enterprise sign-in
      // completes. Consume + clear so it never affects subsequent resolutions.
      const ssoForce = sessionStorage.getItem('ssoSignInWorkspaceId');
      if (ssoForce) sessionStorage.removeItem('ssoSignInWorkspaceId');
      picked = pickWorkspaceOnSignIn(workspaces, savedWorkspaceId, ssoForce);
    }

    if (picked) {
      localStorage.setItem('currentWorkspaceId', picked.id);
      savedWorkspaceIdRef.current = picked.id;
      setCurrentWorkspace(picked);
      hasResolvedThisSessionRef.current = true;
      sessionStorage.setItem('workspaceResolvedSession', '1');
    }
  }, [workspaces, currentWorkspace?.id, currentWorkspace?.name]);


  // Switch workspace mutation
  const switchWorkspaceMutation = useMutation({
    mutationFn: switchWorkspaceAction,
    onMutate: async (workspaceId) => {
      const workspace = workspaces.find((w) => w.id === workspaceId);
      if (!workspace || workspace.id === currentWorkspace?.id) {
        throw new Error('Invalid workspace or already current');
      }
      
      // Optimistically update current workspace
      setCurrentWorkspace(workspace);
      
      return { previousWorkspace: currentWorkspace };
    },
    onSuccess: (_, workspaceId) => {
      const workspace = workspaces.find((w) => w.id === workspaceId);
      if (workspace) {
        toast({
          description: `Switched to ${workspace.name}`,
        });
      }
    },
    onError: (error, _, context) => {
      // Rollback on error
      if (context?.previousWorkspace) {
        setCurrentWorkspace(context.previousWorkspace);
      }
      
      console.error('Error switching workspace:', error);
      toast({
        title: "Error",
        description: "Failed to switch workspace",
        variant: "destructive",
      });
      
      // Refetch workspaces on error
      refetch();
    },
  });

  // Set default workspace mutation
  const setDefaultMutation = useMutation({
    mutationFn: async (workspaceId: string) => {
      const { error } = await supabase.rpc('set_default_workspace', {
        p_workspace_id: workspaceId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      refetch();
      toast({ description: 'Default workspace updated' });
    },
    onError: (err) => {
      console.error('Error setting default workspace:', err);
      captureError(err as Error, { feature: 'set-default-workspace' });
      toast({
        title: 'Error',
        description: 'Failed to set default workspace',
        variant: 'destructive',
      });
    },
  });

  // Show error toast when there's an error
  if (error && !loading) {
    toast({
      title: "Error",
      description: "Failed to load workspaces",
      variant: "destructive",
    });
  }

  const switchWorkspace = useCallback((workspaceId: string) => {
    switchWorkspaceMutation.mutate(workspaceId);
  }, [switchWorkspaceMutation]);

  const setDefaultWorkspace = useCallback(
    (workspaceId: string) => setDefaultMutation.mutate(workspaceId),
    [setDefaultMutation],
  );

  const defaultWorkspaceId =
    workspaces.find(w => w.is_default === true)?.id ?? null;

  return {
    workspaces,
    currentWorkspace,
    defaultWorkspaceId,
    loading: loading || switchWorkspaceMutation.isPending,
    switchWorkspace,
    setDefaultWorkspace,
    refetch: () => refetch(),
  };
}